'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { useHotkey } from '@tanstack/react-hotkeys';
import { Loader2, MessageCircleMore, Sparkles } from 'lucide-react';
import { AnimatePresence, motion, useDragControls } from 'motion/react';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
import Portal from '~/components/primitives/Portal';
import useBlockBodyScrolling from '~/components/primitives/hooks/useBlockBodyScrolling';
import { useActivitySearchMode } from '../useActivitySearchMode';
import { useActivityAiActiveChat } from '../useActivityAiActiveChat';
import { ActivityAiChatPanel } from './ActivityAiChatPanel';
import { ActivityAiChatHeader } from './ActivityAiChatHeader';

/**
 * Mobile-only fixed-bottom CTA shown in AI mode. Tapping it opens a drawer with the
 * full {@link ActivityAiChatPanel}. Mirrors what desktop users get from the sticky
 * AI sidebar — the hero composer scrolls away on mobile, so this gives a persistent
 * way back into the conversation.
 *
 * Visibility: renders when `isAi` AND a chat exists. Shown on mobile AND tablet
 * (`lg:hidden`) — the desktop floating composer only appears at `lg`, so without
 * this the tablet range (sm–lg) had no way back into the conversation.
 */
export const ActivityAiMobileCtaBar: React.FC = () => {
  const $ = _c(22);
  const i18n = useI18n();
  const {
    isAi
  } = useActivitySearchMode();
  const aiChat = useActivityAiActiveChat();
  const {
    messages,
    chatId,
    status
  } = aiChat;
  const [open, setOpen] = useState(false);
  const sheetOpen = isAi && open;
  const hasMessages = chatId.length > 0 || messages.length > 0;
  const isThinking = status === "submitted" || status === "streaming";
  let t0;
  bb0: {
    if (!isThinking) {
      t0 = "";
      break bb0;
    }
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.role === "user") {
        t0 = message.text;
        break bb0;
      }
    }
    t0 = "";
  }
  const inFlightPrompt = t0;
  const visible = isAi && hasMessages;
  let t1;
  if ($[0] !== i18n) {
    t1 = i18n._("activity.search.ai.mobileCtaThinking", undefined, {
      message: "Searching\u2026"
    });
    $[0] = i18n;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const thinkingLabel = t1;
  let t2;
  if ($[2] !== hasMessages || $[3] !== i18n || $[4] !== inFlightPrompt || $[5] !== isThinking || $[6] !== thinkingLabel) {
    t2 = isThinking ? inFlightPrompt || thinkingLabel : hasMessages ? i18n._("activity.search.ai.mobileCtaContinue", undefined, {
      message: "Continue chat"
    }) : i18n._("activity.search.ai.mobileCtaStart", undefined, {
      message: "Zoeken met AI"
    });
    $[2] = hasMessages;
    $[3] = i18n;
    $[4] = inFlightPrompt;
    $[5] = isThinking;
    $[6] = thinkingLabel;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const ctaLabel = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(true);
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleOpen = t3;
  let t4;
  if ($[9] !== ctaLabel || $[10] !== isThinking || $[11] !== visible) {
    t4 = visible && <motion.div key="activity-ai-mobile-cta" initial={{
      y: "120%"
    }} animate={{
      y: 0
    }} exit={{
      y: "120%"
    }} transition={{
      duration: 0.25,
      ease: "easeOut"
    }} className={cn("fixed inset-x-0 bottom-0 z-modals px-4 pb-4 pt-2 lg:hidden", "pointer-events-none")}><div className="bg-linear-to-t pointer-events-none absolute inset-x-0 bottom-0 h-28 from-white via-white/95 to-white/0" aria-hidden={true} /><motion.button type="button" onClick={handleOpen} data-testid="ai-mobile-cta" data-thinking={isThinking ? "true" : "false"} drag="y" dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={{
        top: 0.5,
        bottom: 0
      }} dragSnapToOrigin={true} onDragEnd={(_, info) => {
        if (info.offset.y < -60 || info.velocity.y < -500) {
          handleOpen();
        }
      }} className={cn("pointer-events-auto relative z-10 flex w-full touch-none items-center gap-3 rounded-full", "bg-white py-2 pl-4 pr-2 text-left text-sm font-semibold text-blue-900", "shadow-xl shadow-black/20 ring-1 ring-blue-900/5", "transition-transform active:scale-[0.98]")}>{isThinking ? <Loader2 className="size-4 shrink-0 animate-spin text-secondary" strokeWidth={2.5} aria-hidden={true} /> : <MessageCircleMore className="size-4 shrink-0 text-blue-900/60" strokeWidth={2.5} aria-hidden={true} />}<span className={cn("flex-1 truncate", isThinking && "font-normal italic text-blue-900/70")} aria-live="polite">{ctaLabel}</span><span className={cn("flex size-9 shrink-0 items-center justify-center rounded-full text-white", isThinking ? "animate-pulse bg-secondary/60" : "bg-secondary")} aria-hidden={true}><Sparkles className="size-4" strokeWidth={2.25} /></span></motion.button></motion.div>;
    $[9] = ctaLabel;
    $[10] = isThinking;
    $[11] = visible;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <AnimatePresence initial={false}>{t4}</AnimatePresence>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== sheetOpen) {
    t6 = sheetOpen && <ActivityAiMobileChatSheet onClose={() => setOpen(false)} />;
    $[15] = sheetOpen;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <AnimatePresence>{t6}</AnimatePresence>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t5 || $[20] !== t7) {
    t8 = <>{t5}{t7}</>;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
const ActivityAiMobileChatSheet: React.FC<{
  onClose: () => void;
}> = t0 => {
  const $ = _c(42);
  const {
    onClose
  } = t0;
  const i18n = useI18n();
  const aiChat = useActivityAiActiveChat();
  const [historyOpen, setHistoryOpen] = useState(false);
  const dragControls = useDragControls();
  useBlockBodyScrolling();
  useHotkey("Escape", onClose);
  const hasActiveThread = aiChat.chatId.length > 0 || aiChat.uiMessages.length > 0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    t2 = {
      opacity: 1
    };
    t3 = {
      opacity: 0
    };
    t4 = {
      duration: 0.2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onClose) {
    t5 = <motion.div key="activity-ai-mobile-sheet-backdrop" initial={t1} animate={t2} exit={t3} transition={t4} onClick={onClose} className="fixed inset-0 z-modals bg-blue-900/30 backdrop-blur-sm" />;
    $[4] = onClose;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== i18n) {
    t6 = i18n._("activity.search.ai.sidebarTitle", undefined, {
      message: "AI search"
    });
    $[6] = i18n;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      y: "100%"
    };
    t8 = {
      y: 0
    };
    t9 = {
      y: "100%"
    };
    t10 = {
      type: "tween",
      duration: 0.28,
      ease: "easeOut"
    };
    $[8] = t10;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t10 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      top: 0,
      bottom: 0
    };
    t12 = {
      top: 0,
      bottom: 0.6
    };
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  let t13;
  if ($[14] !== onClose) {
    t13 = (_, info) => {
      if (info.offset.y > 120 || info.velocity.y > 600) {
        onClose();
      }
    };
    $[14] = onClose;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== dragControls) {
    t14 = e => dragControls.start(e);
    $[16] = dragControls;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="h-1 w-10 rounded-full bg-blue-900/15" />;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== t14) {
    t16 = <div onPointerDown={t14} className="flex shrink-0 cursor-grab touch-none justify-center pb-1 pt-2 active:cursor-grabbing" aria-hidden={true}>{t15}</div>;
    $[19] = t14;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== aiChat.goToAllChats || $[22] !== aiChat.threadTitle || $[23] !== hasActiveThread || $[24] !== historyOpen || $[25] !== onClose) {
    t17 = historyOpen ? null : <div className="pt-3"><ActivityAiChatHeader hasActiveThread={hasActiveThread} threadTitle={aiChat.threadTitle} onBack={aiChat.goToAllChats} onClose={onClose} /></div>;
    $[21] = aiChat.goToAllChats;
    $[22] = aiChat.threadTitle;
    $[23] = hasActiveThread;
    $[24] = historyOpen;
    $[25] = onClose;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setHistoryOpen(true);
    t19 = () => setHistoryOpen(false);
    $[27] = t18;
    $[28] = t19;
  } else {
    t18 = $[27];
    t19 = $[28];
  }
  let t20;
  if ($[29] !== historyOpen || $[30] !== onClose) {
    t20 = <ActivityAiChatPanel variant="mobile" onCloseDrawer={onClose} historyOpen={historyOpen} onOpenHistory={t18} onCloseHistory={t19} />;
    $[29] = historyOpen;
    $[30] = onClose;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== dragControls || $[33] !== t13 || $[34] !== t16 || $[35] !== t17 || $[36] !== t20 || $[37] !== t6) {
    t21 = <motion.div key="activity-ai-mobile-sheet" role="dialog" aria-modal="true" aria-label={t6} initial={t7} animate={t8} exit={t9} transition={t10} drag="y" dragControls={dragControls} dragListener={false} dragConstraints={t11} dragElastic={t12} onDragEnd={t13} className="fixed inset-x-0 bottom-0 z-modals flex h-[85dvh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl">{t16}{t17}{t20}</motion.div>;
    $[32] = dragControls;
    $[33] = t13;
    $[34] = t16;
    $[35] = t17;
    $[36] = t20;
    $[37] = t6;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t21 || $[40] !== t5) {
    t22 = <Portal querySelector="#modals">{t5}{t21}</Portal>;
    $[39] = t21;
    $[40] = t5;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  return t22;
};